<template>
    <AdminPageInner v-if="loaded" :breadcrumbs="breadcrumbs">
        <template #heading>Users</template>
        <template #links>
            <router-link
                v-if="$can('create', 'User')"
                :to="{name: 'admin.users.create'}">
                Create
            </router-link>
        </template>
        <PaginationWrapper
            ref="pagination"
            class="mt-4"
            :rows="rows"
            :constraints="constraints"
            :meta="meta">
            <DataComponent
                class="w-full mt-6"
                :data="rows"
                :initialSort="initialSort"
                @sort="onSort">
                <DataField name="firstname" label="First name" class="md:w-1/4">
                    <template v-slot="{row, sortable}">
                        <AppLink v-if="row.user_can.view" :to="{name: 'admin.users.show', params: {userId: row.id}}">
                            {{ row.firstname }}
                        </AppLink>
                        <span v-else>{{ row.firstname }}</span>
                    </template>
                </DataField>
                <DataField name="lastname" label="Last name" class="md:w-1/4">
                    <template v-slot="{row, sortable}">
                        <AppLink v-if="row.user_can.view" :to="{name: 'admin.users.show', params: {userId: row.id}}">
                            {{ row.lastname }}
                        </AppLink>
                        <span v-else>{{ row.lastname }}</span>
                    </template>
                </DataField>
                <DataField name="email" class="md:w-1/4">
                    <template v-slot="{row, sortable}">
                        {{ row.email }}
                    </template>
                </DataField>
                <DataField name="credits" class="md:w-1/4">
                    <template v-slot="{row, sortable}">
                        {{ row.credits }}
                    </template>
                </DataField>
                <DataField name="actions" :sortable="false" :showLabel="false" :showResponsiveLabel="false">
                    <template v-slot="{row}">
                        <div class="flex justify-around md:justify-end items-center">
                            <button v-if="!row.registration_completed_at && canResend"
                                    title="Resend the Welcome Notification."
                                    class="text-gray-700 mr-4"
                                    :disabled="resendQueue.includes(row.id)"
                                    @click.prevent="confirmResendWelcomeNotification(row)">
                                <font-awesome-icon v-if="!resendQueue.includes(row.id)" icon="paper-plane" size="sm"/>
                                <font-awesome-icon v-else icon="spinner" :spin="true" size="sm"/>
                            </button>
                            <router-link
                                v-if="row.user_can.update"
                                class="text-indigo-600 hover:text-indigo-900 mr-4"
                                :to="{name: 'admin.users.edit', params: {userId: row.id}}">
                                Edit
                            </router-link>
                            <button
                                v-if="row.user_can.delete"
                                class="text-red-600" @click.prevent="confirmDelete(row)">Delete
                            </button>
                        </div>
                    </template>
                </DataField>
            </DataComponent>
        </PaginationWrapper>
    </AdminPageInner>
</template>

<script>

import axios from "axios";
import AdminPageInner from "@/Components/Layout/AdminPageInner.vue";
import PaginationWrapper from "@/Components/Layout/PaginationWrapper.vue";
import PaginationPage from "@/Mixins/PaginationPage";
import {DataComponent, DataField} from "@/Components/DataComponent";
import CalendarFormat from "@/Components/CalendarFormat";
import Breadcrumbs from "@/Components/Breadcrumbs";
import AppLink from "@/Components/AppLink";

export default {

    name: "UserIndexPage",

    mixins: [PaginationPage],

    data() {
        return {
            resendQueue: []
        };
    },

    methods: {
        async confirmResendWelcomeNotification(user) {

            if (user.registration_completed_at || !this.canResend || this.resendQueue.includes(user.id)) {
                return;
            }

            if (window.confirm(`Are you sure you want to resend the Welcome Notification to ${user.name}?`)) {

                this.resendQueue.push(user.id);

                try {
                    await axios.post(route('api.users.resend-welcome', user));
                    this.$notify.success('Welcome Notification resent successfully.');
                } catch (e) {
                    console.error(e);
                    this.$notify.warning('Unable to resend Welcome Notification.');
                }
                this.resendQueue = this.resendQueue.filter(id => id !== user.id);

            }
        },
        confirmDelete(user) {
            if (window.confirm('Are you sure you want to delete this User?')) {
                axios.delete(route('api.users.destroy', user))
                    .then(() => {
                        this.$notify.success('User deleted');
                        this.$router.push({
                            name: this.$route.name,
                            query: _.assign({}, this.$route.query, {t: dayjs().unix()})
                        });
                    })
                    .catch(console.error);


            }
        }
    },

    computed: {
        breadcrumbs() {
            return [
                {label: 'Admin', to: {name: 'admin'}},
                {label: 'Users', to: {name: this.$route.name}},
            ];
        },
        canResend() {
            return this.$can('create', 'User')
        }
    },

    components: {
        AdminPageInner,
        PaginationWrapper,
        DataComponent,
        DataField,
        CalendarFormat,
        Breadcrumbs,
        AppLink
    },

    async beforeRouteEnter(to, from, next) {
        const data = (await axios.get(route('api.users.index'), {params: to.query})).data;
        next(vm => vm.setData(data));
    },

    async beforeRouteUpdate(to, from) {
        const data = (await axios.get(route('api.users.index'), {params: to.query})).data;
        this.setData(data);
    },
};

</script>
